import { DOCUMENT } from '@angular/common';
import {
    Directive,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    forwardRef,
    Inject,
} from '@angular/core';
import {
    ControlValueAccessor,
    FormControl,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator,
} from '@angular/forms';

import { CustomKeyboardEvent } from './custom-keyboard-event';
import { IConfig, NGX_MASK_CONFIG, timeMasks, withoutValidation } from './ngx-mask.config';
import { NgxMaskService } from './ngx-mask.service';
import { MaskExpression } from './ngx-mask-expression.enum';

interface IOnChage {
    (_: any): void;
}
interface IOnTouch {
    (): void;
}
// tslint:disable deprecation
// tslint:disable no-input-rename
@Directive({
    selector: 'input[mask], textarea[mask]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => NgxMaskDirective),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => NgxMaskDirective),
            multi: true,
        },
        NgxMaskService,
    ],
    exportAs: 'mask,ngxMask',
})
export class NgxMaskDirective implements ControlValueAccessor, OnChanges, Validator {
    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input('mask') public maskExpression: string | undefined | null = '';

    @Input() public specialCharacters: IConfig['specialCharacters'] = [];

    @Input() public patterns: IConfig['patterns'] = {};

    @Input() public prefix: IConfig['prefix'] = '';

    @Input() public suffix: IConfig['suffix'] = '';

    @Input() public thousandSeparator: IConfig['thousandSeparator'] = ' ';

    @Input() public decimalMarker: IConfig['decimalMarker'] = '.';

    @Input() public dropSpecialCharacters: IConfig['dropSpecialCharacters'] | null = null;

    @Input() public hiddenInput: IConfig['hiddenInput'] | null = null;

    @Input() public showMaskTyped: IConfig['showMaskTyped'] | null = null;

    @Input() public placeHolderCharacter: IConfig['placeHolderCharacter'] | null = null;

    @Input() public shownMaskExpression: IConfig['shownMaskExpression'] | null = null;

    @Input() public showTemplate: IConfig['showTemplate'] | null = null;

    @Input() public clearIfNotMatch: IConfig['clearIfNotMatch'] | null = null;

    @Input() public validation: IConfig['validation'] | null = null;

    @Input() public separatorLimit: IConfig['separatorLimit'] | null = null;

    @Input() public allowNegativeNumbers: IConfig['allowNegativeNumbers'] | null = null;

    @Input() public leadZeroDateTime: IConfig['leadZeroDateTime'] | null = null;

    @Input() public leadZero: IConfig['leadZero'] | null = null;

    @Input() public triggerOnMaskChange: IConfig['triggerOnMaskChange'] | null = null;

    @Input() public apm: IConfig['apm'] | null = null;

    @Input() public inputTransformFn: IConfig['inputTransformFn'] | null = null;

    @Input() public outputTransformFn: IConfig['outputTransformFn'] | null = null;

    @Input() public keepCharacterPositions: IConfig['keepCharacterPositions'] | null = null;

    @Output() public maskFilled: IConfig['maskFilled'] = new EventEmitter<void>();

    private _maskValue = '';

    private _isFocused = false;

    private _inputValue!: string;

    private _position: number | null = null;

    private _code!: string;

    private _maskExpressionArray: string[] = [];

    private _justPasted = false;

    /**For IME composition event */
    private _isComposing = false;

    public constructor(
        // tslint:disable-next-line
        @Inject(DOCUMENT) private document: any,
        @Inject(NGX_MASK_CONFIG) protected _config: IConfig,
        public _maskService: NgxMaskService
    ) {}

    // eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-explicit-any
    public onChange: IOnChage = (_: any) => {};

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    public onTouch: IOnTouch = () => {};

    public ngOnChanges(changes: SimpleChanges): void {
        const {
            maskExpression,
            specialCharacters,
            patterns,
            prefix,
            suffix,
            thousandSeparator,
            decimalMarker,
            dropSpecialCharacters,
            hiddenInput,
            showMaskTyped,
            placeHolderCharacter,
            shownMaskExpression,
            showTemplate,
            clearIfNotMatch,
            validation,
            separatorLimit,
            allowNegativeNumbers,
            leadZeroDateTime,
            leadZero,
            triggerOnMaskChange,
            apm,
            inputTransformFn,
            outputTransformFn,
            keepCharacterPositions,
        } = changes;
        if (maskExpression) {
            if (
                maskExpression.currentValue !== maskExpression.previousValue &&
                !maskExpression.firstChange
            ) {
                this._maskService.maskChanged = true;
            }
            if (
                maskExpression.currentValue &&
                maskExpression.currentValue.split(MaskExpression.OR).length > 1
            ) {
                this._maskExpressionArray = maskExpression.currentValue
                    .split(MaskExpression.OR)
                    .sort((a: string, b: string) => {
                        return a.length - b.length;
                    });
                this._setMask();
            } else {
                this._maskExpressionArray = [];
                this._maskValue = maskExpression.currentValue || MaskExpression.EMPTY_STRING;
                this._maskService.maskExpression = this._maskValue;
            }
        }
        if (specialCharacters) {
            if (!specialCharacters.currentValue || !Array.isArray(specialCharacters.currentValue)) {
                return;
            } else {
                this._maskService.specialCharacters = specialCharacters.currentValue || [];
            }
        }
        if (allowNegativeNumbers) {
            this._maskService.allowNegativeNumbers = allowNegativeNumbers.currentValue;
            if (this._maskService.allowNegativeNumbers) {
                this._maskService.specialCharacters = this._maskService.specialCharacters.filter(
                    (c: string) => c !== MaskExpression.MINUS
                );
            }
        }
        // Only overwrite the mask available patterns if a pattern has actually been passed in
        if (patterns && patterns.currentValue) {
            this._maskService.patterns = patterns.currentValue;
        }
        if (apm && apm.currentValue) {
            this._maskService.apm = apm.currentValue;
        }
        if (prefix) {
            this._maskService.prefix = prefix.currentValue;
        }
        if (suffix) {
            this._maskService.suffix = suffix.currentValue;
        }
        if (thousandSeparator) {
            this._maskService.thousandSeparator = thousandSeparator.currentValue;
        }
        if (decimalMarker) {
            this._maskService.decimalMarker = decimalMarker.currentValue;
        }
        if (dropSpecialCharacters) {
            this._maskService.dropSpecialCharacters = dropSpecialCharacters.currentValue;
        }
        if (hiddenInput) {
            this._maskService.hiddenInput = hiddenInput.currentValue;
        }
        if (showMaskTyped) {
            this._maskService.showMaskTyped = showMaskTyped.currentValue;
            if (
                showMaskTyped.previousValue === false &&
                showMaskTyped.currentValue === true &&
                this._isFocused
            ) {
                requestAnimationFrame(() => {
                    this._maskService._elementRef?.nativeElement.click();
                });
            }
        }
        if (placeHolderCharacter) {
            this._maskService.placeHolderCharacter = placeHolderCharacter.currentValue;
        }
        if (shownMaskExpression) {
            this._maskService.shownMaskExpression = shownMaskExpression.currentValue;
        }
        if (showTemplate) {
            this._maskService.showTemplate = showTemplate.currentValue;
        }
        if (clearIfNotMatch) {
            this._maskService.clearIfNotMatch = clearIfNotMatch.currentValue;
        }
        if (validation) {
            this._maskService.validation = validation.currentValue;
        }
        if (separatorLimit) {
            this._maskService.separatorLimit = separatorLimit.currentValue;
        }
        if (leadZeroDateTime) {
            this._maskService.leadZeroDateTime = leadZeroDateTime.currentValue;
        }
        if (leadZero) {
            this._maskService.leadZero = leadZero.currentValue;
        }
        if (triggerOnMaskChange) {
            this._maskService.triggerOnMaskChange = triggerOnMaskChange.currentValue;
        }
        if (inputTransformFn) {
            this._maskService.inputTransformFn = inputTransformFn.currentValue;
        }
        if (outputTransformFn) {
            this._maskService.outputTransformFn = outputTransformFn.currentValue;
        }
        if (keepCharacterPositions) {
            this._maskService.keepCharacterPositions = keepCharacterPositions.currentValue;
        }
        this._applyMask();
    }

    // eslint-disable-next-line complexity
    public validate({ value }: FormControl): ValidationErrors | null {
        if (!this._maskService.validation || !this._maskValue) {
            return null;
        }
        if (this._maskService.ipError) {
            return this._createValidationError(value);
        }
        if (this._maskService.cpfCnpjError) {
            return this._createValidationError(value);
        }
        if (this._maskValue.startsWith(MaskExpression.SEPARATOR)) {
            return null;
        }
        if (withoutValidation.includes(this._maskValue)) {
            return null;
        }
        if (this._maskService.clearIfNotMatch) {
            return null;
        }
        if (timeMasks.includes(this._maskValue)) {
            return this._validateTime(value);
        }
        if (value && value.toString().length >= 1) {
            let counterOfOpt = 0;
            if (this._maskValue.startsWith(MaskExpression.PERCENT)) {
                return null;
            }
            for (const key in this._maskService.patterns) {
                if (this._maskService.patterns[key]?.optional) {
                    if (this._maskValue.indexOf(key) !== this._maskValue.lastIndexOf(key)) {
                        const opt: string = this._maskValue
                            .split(MaskExpression.EMPTY_STRING)
                            .filter((i: string) => i === key)
                            .join(MaskExpression.EMPTY_STRING);
                        counterOfOpt += opt.length;
                    } else if (this._maskValue.indexOf(key) !== -1) {
                        counterOfOpt++;
                    }
                    if (
                        this._maskValue.indexOf(key) !== -1 &&
                        value.toString().length >= this._maskValue.indexOf(key)
                    ) {
                        return null;
                    }
                    if (counterOfOpt === this._maskValue.length) {
                        return null;
                    }
                }
            }
            if (
                this._maskValue.indexOf(MaskExpression.CURLY_BRACKETS_LEFT) === 1 &&
                value.toString().length ===
                    this._maskValue.length +
                        Number(
                            (
                                this._maskValue.split(MaskExpression.CURLY_BRACKETS_LEFT)[1] ??
                                MaskExpression.EMPTY_STRING
                            ).split(MaskExpression.CURLY_BRACKETS_RIGHT)[0]
                        ) -
                        4
            ) {
                return null;
            } else if (
                (this._maskValue.indexOf(MaskExpression.SYMBOL_STAR) > 1 &&
                    value.toString().length <
                        this._maskValue.indexOf(MaskExpression.SYMBOL_STAR)) ||
                (this._maskValue.indexOf(MaskExpression.SYMBOL_QUESTION) > 1 &&
                    value.toString().length <
                        this._maskValue.indexOf(MaskExpression.SYMBOL_QUESTION)) ||
                this._maskValue.indexOf(MaskExpression.CURLY_BRACKETS_LEFT) === 1
            ) {
                return this._createValidationError(value);
            }
            if (
                this._maskValue.indexOf(MaskExpression.SYMBOL_STAR) === -1 ||
                this._maskValue.indexOf(MaskExpression.SYMBOL_QUESTION) === -1
            ) {
                // eslint-disable-next-line no-param-reassign
                value = typeof value === 'number' ? String(value) : value;
                const array = this._maskValue.split('*');
                const length: number = this._maskService.dropSpecialCharacters
                    ? this._maskValue.length -
                      this._maskService.checkDropSpecialCharAmount(this._maskValue) -
                      counterOfOpt
                    : this.prefix
                    ? this._maskValue.length + this.prefix.length - counterOfOpt
                    : this._maskValue.length - counterOfOpt;

                if (array.length === 1) {
                    if (value.toString().length < length) {
                        return this._createValidationError(value);
                    }
                }
                if (array.length > 1) {
                    const lastIndexArray = array[array.length - 1];
                    if (
                        lastIndexArray &&
                        this._maskService.specialCharacters.includes(lastIndexArray[0] as string) &&
                        String(value).includes(lastIndexArray[0] ?? '') &&
                        !this.dropSpecialCharacters
                    ) {
                        const special = value.split(lastIndexArray[0]);
                        return special[special.length - 1].length === lastIndexArray.length - 1
                            ? null
                            : this._createValidationError(value);
                    } else if (
                        ((lastIndexArray &&
                            !this._maskService.specialCharacters.includes(
                                lastIndexArray[0] as string
                            )) ||
                            !lastIndexArray ||
                            this._maskService.dropSpecialCharacters) &&
                        value.length >= length - 1
                    ) {
                        return null;
                    } else {
                        return this._createValidationError(value);
                    }
                }
            }
            if (
                this._maskValue.indexOf(MaskExpression.SYMBOL_STAR) === 1 ||
                this._maskValue.indexOf(MaskExpression.SYMBOL_QUESTION) === 1
            ) {
                return null;
            }
        }
        if (value) {
            this.maskFilled.emit();
            return null;
        }
        return null;
    }

    @HostListener('paste')
    public onPaste() {
        this._justPasted = true;
    }

    @HostListener('focus', ['$event']) public onFocus() {
        this._isFocused = true;
    }

    @HostListener('ngModelChange', ['$event'])
    public onModelChange(value: string | undefined | null | number): void {
        // on form reset we need to update the actualValue
        if (
            (value === MaskExpression.EMPTY_STRING || value === null || value === undefined) &&
            this._maskService.actualValue
        ) {
            this._maskService.actualValue = this._maskService.getActualValue(
                MaskExpression.EMPTY_STRING
            );
        }
    }

    @HostListener('input', ['$event'])
    public onInput(e: CustomKeyboardEvent): void {
        // If IME is composing text, we wait for the composed text.
        if (this._isComposing) return;
        const el: HTMLInputElement = e.target as HTMLInputElement;
        const transformedValue = this._maskService.inputTransformFn(el.value);
        if (el.type !== 'number') {
            if (typeof transformedValue === 'string' || typeof transformedValue === 'number') {
                el.value = transformedValue.toString();

                this._inputValue = el.value;
                this._setMask();

                if (!this._maskValue) {
                    this.onChange(el.value);
                    return;
                }

                let position: number =
                    el.selectionStart === 1
                        ? (el.selectionStart as number) + this._maskService.prefix.length
                        : (el.selectionStart as number);

                if (
                    this.showMaskTyped &&
                    this.keepCharacterPositions &&
                    this._maskService.placeHolderCharacter.length === 1
                ) {
                    const inputSymbol = el.value.slice(position - 1, position);
                    const prefixLength = this.prefix.length;
                    const checkSymbols: boolean = this._maskService._checkSymbolMask(
                        inputSymbol,
                        this._maskService.maskExpression[position - 1 - prefixLength] ??
                            MaskExpression.EMPTY_STRING
                    );

                    const checkSpecialCharacter: boolean = this._maskService._checkSymbolMask(
                        inputSymbol,
                        this._maskService.maskExpression[position + 1 - prefixLength] ??
                            MaskExpression.EMPTY_STRING
                    );
                    const selectRangeBackspace: boolean =
                        this._maskService.selStart === this._maskService.selEnd;
                    const selStart = Number(this._maskService.selStart) - prefixLength ?? '';
                    const selEnd = Number(this._maskService.selEnd) - prefixLength ?? '';

                    if (this._code === MaskExpression.BACKSPACE) {
                        if (!selectRangeBackspace) {
                            if (this._maskService.selStart === prefixLength) {
                                this._maskService.actualValue =
                                    this.prefix +
                                    this._maskService.maskIsShown.slice(0, selEnd) +
                                    this._inputValue.split(this.prefix).join('');
                            } else if (
                                this._maskService.selStart ===
                                this._maskService.maskIsShown.length + prefixLength
                            ) {
                                this._maskService.actualValue =
                                    this._inputValue +
                                    this._maskService.maskIsShown.slice(selStart, selEnd);
                            } else {
                                this._maskService.actualValue =
                                    this.prefix +
                                    this._inputValue
                                        .split(this.prefix)
                                        .join('')
                                        .slice(0, selStart) +
                                    this._maskService.maskIsShown.slice(selStart, selEnd) +
                                    this._maskService.actualValue.slice(
                                        selEnd + prefixLength,
                                        this._maskService.maskIsShown.length + prefixLength
                                    ) +
                                    this.suffix;
                            }
                        } else if (
                            !this._maskService.specialCharacters.includes(
                                this._maskService.maskExpression.slice(
                                    position - this.prefix.length,
                                    position + 1 - this.prefix.length
                                )
                            ) &&
                            selectRangeBackspace
                        ) {
                            if (selStart === 1 && this.prefix) {
                                this._maskService.actualValue =
                                    this.prefix +
                                    this._maskService.placeHolderCharacter +
                                    el.value
                                        .split(this.prefix)
                                        .join('')
                                        .split(this.suffix)
                                        .join('') +
                                    this.suffix;
                                position = position - 1;
                            } else {
                                const part1 = el.value.substring(0, position);
                                const part2 = el.value.substring(position);
                                this._maskService.actualValue =
                                    part1 + this._maskService.placeHolderCharacter + part2;
                            }
                        }
                    }
                    if (this._code !== MaskExpression.BACKSPACE) {
                        if (!checkSymbols && !checkSpecialCharacter && selectRangeBackspace) {
                            position = Number(el.selectionStart) - 1;
                        } else if (
                            this._maskService.specialCharacters.includes(
                                el.value.slice(position, position + 1)
                            ) &&
                            checkSpecialCharacter &&
                            !this._maskService.specialCharacters.includes(
                                el.value.slice(position + 1, position + 2)
                            )
                        ) {
                            this._maskService.actualValue =
                                el.value.slice(0, position - 1) +
                                el.value.slice(position, position + 1) +
                                inputSymbol +
                                el.value.slice(position + 2);
                            position = position + 1;
                        } else if (checkSymbols) {
                            if (el.value.length === 1 && position === 1) {
                                this._maskService.actualValue =
                                    this.prefix +
                                    inputSymbol +
                                    this._maskService.maskIsShown.slice(
                                        1,
                                        this._maskService.maskIsShown.length
                                    ) +
                                    this.suffix;
                            } else {
                                this._maskService.actualValue =
                                    el.value.slice(0, position - 1) +
                                    inputSymbol +
                                    el.value
                                        .slice(position + 1)
                                        .split(this.suffix)
                                        .join('') +
                                    this.suffix;
                            }
                        } else if (
                            this.prefix &&
                            el.value.length === 1 &&
                            position - prefixLength === 1 &&
                            this._maskService._checkSymbolMask(
                                el.value,
                                this._maskService.maskExpression[position - 1 - prefixLength] ??
                                    MaskExpression.EMPTY_STRING
                            )
                        ) {
                            this._maskService.actualValue =
                                this.prefix +
                                el.value +
                                this._maskService.maskIsShown.slice(
                                    1,
                                    this._maskService.maskIsShown.length
                                ) +
                                this.suffix;
                        }
                    }
                }

                let caretShift = 0;
                let backspaceShift = false;
                if (this._code === MaskExpression.DELETE && MaskExpression.SEPARATOR) {
                    this._maskService.deletedSpecialCharacter = true;
                }
                if (
                    this._inputValue.length >= this._maskService.maskExpression.length - 1 &&
                    this._code !== MaskExpression.BACKSPACE &&
                    this._maskService.maskExpression === MaskExpression.DAYS_MONTHS_YEARS &&
                    position < 10
                ) {
                    const inputSymbol = this._inputValue.slice(position - 1, position);
                    el.value =
                        this._inputValue.slice(0, position - 1) +
                        inputSymbol +
                        this._inputValue.slice(position + 1);
                }
                if (
                    this._maskService.maskExpression === MaskExpression.DAYS_MONTHS_YEARS &&
                    this.leadZeroDateTime
                ) {
                    if (
                        (position < 3 && Number(el.value) > 31 && Number(el.value) < 40) ||
                        (position === 5 && Number(el.value.slice(3, 5)) > 12)
                    ) {
                        position = position + 2;
                    }
                }

                if (
                    this._maskService.maskExpression === MaskExpression.HOURS_MINUTES_SECONDS &&
                    this.apm
                ) {
                    if (this._justPasted && el.value.slice(0, 2) === MaskExpression.DOUBLE_ZERO) {
                        el.value = el.value.slice(1, 2) + el.value.slice(2, el.value.length);
                    }
                    el.value =
                        el.value === MaskExpression.DOUBLE_ZERO
                            ? MaskExpression.NUMBER_ZERO
                            : el.value;
                }

                this._maskService.applyValueChanges(
                    position,
                    this._justPasted,
                    this._code === MaskExpression.BACKSPACE || this._code === MaskExpression.DELETE,
                    (shift: number, _backspaceShift: boolean) => {
                        this._justPasted = false;
                        caretShift = shift;
                        backspaceShift = _backspaceShift;
                    }
                );
                // only set the selection if the element is active
                if (this._getActiveElement() !== el) {
                    return;
                }

                if (this._maskService.plusOnePosition) {
                    position = position + 1;
                    this._maskService.plusOnePosition = false;
                }
                // update position after applyValueChanges to prevent cursor on wrong position when it has an array of maskExpression
                if (this._maskExpressionArray.length) {
                    if (this._code === MaskExpression.BACKSPACE) {
                        position = this.specialCharacters.includes(
                            this._inputValue.slice(position - 1, position)
                        )
                            ? position - 1
                            : position;
                    } else {
                        position =
                            el.selectionStart === 1
                                ? (el.selectionStart as number) + this._maskService.prefix.length
                                : (el.selectionStart as number);
                    }
                }
                this._position =
                    this._position === 1 && this._inputValue.length === 1 ? null : this._position;
                let positionToApply: number = this._position
                    ? this._inputValue.length + position + caretShift
                    : position +
                      (this._code === MaskExpression.BACKSPACE && !backspaceShift ? 0 : caretShift);
                if (positionToApply > this._getActualInputLength()) {
                    positionToApply =
                        el.value === this._maskService.decimalMarker && el.value.length === 1
                            ? this._getActualInputLength() + 1
                            : this._getActualInputLength();
                }
                if (positionToApply < 0) {
                    positionToApply = 0;
                }
                el.setSelectionRange(positionToApply, positionToApply);
                this._position = null;
            } else {
                console.warn(
                    'Ngx-mask writeValue work with string | number, your current value:',
                    typeof transformedValue
                );
            }
        } else {
            if (!this._maskValue) {
                this.onChange(el.value);
                return;
            }
            this._maskService.applyValueChanges(
                el.value.length,
                this._justPasted,
                this._code === MaskExpression.BACKSPACE || this._code === MaskExpression.DELETE
            );
        }
    }

    // IME starts
    @HostListener('compositionstart', ['$event'])
    public onCompositionStart(): void {
        this._isComposing = true;
    }

    // IME completes
    @HostListener('compositionend', ['$event'])
    public onCompositionEnd(e: CustomKeyboardEvent): void {
        this._isComposing = false;
        this._justPasted = true;
        this.onInput(e);
    }

    @HostListener('blur', ['$event'])
    public onBlur(e: CustomKeyboardEvent): void {
        if (this._maskValue) {
            const el: HTMLInputElement = e.target as HTMLInputElement;
            if (this.leadZero && el.value.length > 0 && typeof this.decimalMarker === 'string') {
                const maskExpression = this._maskService.maskExpression;
                const precision = Number(
                    this._maskService.maskExpression.slice(
                        maskExpression.length - 1,
                        maskExpression.length
                    )
                );
                if (precision > 1) {
                    el.value = this.suffix ? el.value.split(this.suffix).join('') : el.value;
                    const decimalPart = el.value.split(this.decimalMarker)[1] as string;
                    el.value = el.value.includes(this.decimalMarker)
                        ? el.value +
                          MaskExpression.NUMBER_ZERO.repeat(precision - decimalPart.length) +
                          this.suffix
                        : el.value +
                          this.decimalMarker +
                          MaskExpression.NUMBER_ZERO.repeat(precision) +
                          this.suffix;
                    this._maskService.actualValue = el.value;
                }
            }
            this._maskService.clearIfNotMatchFn();
        }
        this._isFocused = false;
        this.onTouch();
    }

    @HostListener('click', ['$event'])
    public onClick(e: MouseEvent | CustomKeyboardEvent): void {
        if (!this._maskValue) {
            return;
        }

        const el: HTMLInputElement = e.target as HTMLInputElement;
        const posStart = 0;
        const posEnd = 0;

        if (
            el !== null &&
            el.selectionStart !== null &&
            el.selectionStart === el.selectionEnd &&
            el.selectionStart > this._maskService.prefix.length &&
            // eslint-disable-next-line
            (e as any).keyCode !== 38
        ) {
            if (this._maskService.showMaskTyped && !this.keepCharacterPositions) {
                // We are showing the mask in the input
                this._maskService.maskIsShown = this._maskService.showMaskInInput();
                if (
                    el.setSelectionRange &&
                    this._maskService.prefix + this._maskService.maskIsShown === el.value
                ) {
                    // the input ONLY contains the mask, so position the cursor at the start
                    el.focus();
                    el.setSelectionRange(posStart, posEnd);
                } else {
                    // the input contains some characters already
                    if (el.selectionStart > this._maskService.actualValue.length) {
                        // if the user clicked beyond our value's length, position the cursor at the end of our value
                        el.setSelectionRange(
                            this._maskService.actualValue.length,
                            this._maskService.actualValue.length
                        );
                    }
                }
            }
        }
        const nextValue: string | null =
            el &&
            (el.value === this._maskService.prefix
                ? this._maskService.prefix + this._maskService.maskIsShown
                : el.value);

        /** Fix of cursor position jumping to end in most browsers no matter where cursor is inserted onFocus */
        if (el && el.value !== nextValue) {
            el.value = nextValue;
        }
        /** fix of cursor position with prefix when mouse click occur */
        if (
            el &&
            el.type !== 'number' &&
            ((el.selectionStart as number) || (el.selectionEnd as number)) <=
                this._maskService.prefix.length
        ) {
            el.selectionStart = this._maskService.prefix.length;
            return;
        }
        /** select only inserted text */
        if (el && (el.selectionEnd as number) > this._getActualInputLength()) {
            el.selectionEnd = this._getActualInputLength();
        }
    }

    // eslint-disable-next-line complexity
    @HostListener('keydown', ['$event'])
    public onKeyDown(e: CustomKeyboardEvent): void {
        if (!this._maskValue) {
            return;
        }

        if (this._isComposing) {
            // User finalize their choice from IME composition, so trigger onInput() for the composed text.
            if (e.key === 'Enter') this.onCompositionEnd(e);
            return;
        }

        this._code = e.code ? e.code : e.key;
        const el: HTMLInputElement = e.target as HTMLInputElement;
        this._inputValue = el.value;
        this._setMask();

        if (el.type !== 'number') {
            if (e.key === MaskExpression.ARROW_UP) {
                e.preventDefault();
            }
            if (
                e.key === MaskExpression.ARROW_LEFT ||
                e.key === MaskExpression.BACKSPACE ||
                e.key === MaskExpression.DELETE
            ) {
                if (e.key === MaskExpression.BACKSPACE && el.value.length === 0) {
                    el.selectionStart = el.selectionEnd;
                }
                if (e.key === MaskExpression.BACKSPACE && (el.selectionStart as number) !== 0) {
                    // If specialChars is false, (shouldn't ever happen) then set to the defaults
                    this.specialCharacters = this.specialCharacters?.length
                        ? this.specialCharacters
                        : this._config.specialCharacters;
                    if (
                        this.prefix.length > 1 &&
                        (el.selectionStart as number) <= this.prefix.length
                    ) {
                        el.setSelectionRange(this.prefix.length, el.selectionEnd);
                    } else {
                        if (
                            this._inputValue.length !== (el.selectionStart as number) &&
                            (el.selectionStart as number) !== 1
                        ) {
                            while (
                                this.specialCharacters.includes(
                                    (
                                        this._inputValue[(el.selectionStart as number) - 1] ??
                                        MaskExpression.EMPTY_STRING
                                    ).toString()
                                ) &&
                                ((this.prefix.length >= 1 &&
                                    (el.selectionStart as number) > this.prefix.length) ||
                                    this.prefix.length === 0)
                            ) {
                                el.setSelectionRange(
                                    (el.selectionStart as number) - 1,
                                    el.selectionEnd
                                );
                            }
                        }
                    }
                }
                this.checkSelectionOnDeletion(el);
                if (
                    this._maskService.prefix.length &&
                    (el.selectionStart as number) <= this._maskService.prefix.length &&
                    (el.selectionEnd as number) <= this._maskService.prefix.length
                ) {
                    e.preventDefault();
                }
                const cursorStart: number | null = el.selectionStart;
                if (
                    e.key === MaskExpression.BACKSPACE &&
                    !el.readOnly &&
                    cursorStart === 0 &&
                    el.selectionEnd === el.value.length &&
                    el.value.length !== 0
                ) {
                    this._position = this._maskService.prefix ? this._maskService.prefix.length : 0;
                    this._maskService.applyMask(
                        this._maskService.prefix,
                        this._maskService.maskExpression,
                        this._position
                    );
                }
            }
            if (
                !!this.suffix &&
                this.suffix.length > 1 &&
                this._inputValue.length - this.suffix.length < (el.selectionStart as number)
            ) {
                el.setSelectionRange(
                    this._inputValue.length - this.suffix.length,
                    this._inputValue.length
                );
            } else if (
                (e.code === 'KeyA' && e.ctrlKey) ||
                (e.code === 'KeyA' && e.metaKey) // Cmd + A (Mac)
            ) {
                el.setSelectionRange(0, this._getActualInputLength());
                e.preventDefault();
            }
            this._maskService.selStart = el.selectionStart;
            this._maskService.selEnd = el.selectionEnd;
        }
    }

    /** It writes the value in the input */
    public async writeValue(
        controlValue: string | number | { value: string | number; disable?: boolean }
    ): Promise<void> {
        if (typeof controlValue === 'object' && controlValue !== null && 'value' in controlValue) {
            if ('disable' in controlValue) {
                this.setDisabledState(Boolean(controlValue.disable));
            }
            // eslint-disable-next-line no-param-reassign
            controlValue = controlValue.value;
        }
        if (controlValue !== null) {
            // eslint-disable-next-line no-param-reassign
            controlValue = this.inputTransformFn
                ? this.inputTransformFn(controlValue)
                : controlValue;
        }

        if (
            typeof controlValue === 'string' ||
            typeof controlValue === 'number' ||
            controlValue === null ||
            controlValue === undefined
        ) {
            if (controlValue === null || controlValue === undefined || controlValue === '') {
                this._maskService._currentValue = '';
                this._maskService._previousValue = '';
            }
            // eslint-disable-next-line no-param-reassign
            let inputValue: string | number | null | undefined = controlValue;
            if (
                typeof inputValue === 'number' ||
                this._maskValue.startsWith(MaskExpression.SEPARATOR)
            ) {
                // eslint-disable-next-line no-param-reassign
                inputValue = String(inputValue);
                const localeDecimalMarker = this._maskService.currentLocaleDecimalMarker();
                if (!Array.isArray(this._maskService.decimalMarker)) {
                    // eslint-disable-next-line no-param-reassign
                    inputValue =
                        this._maskService.decimalMarker !== localeDecimalMarker
                            ? inputValue.replace(
                                  localeDecimalMarker,
                                  this._maskService.decimalMarker
                              )
                            : inputValue;
                }
                if (
                    this._maskService.leadZero &&
                    inputValue &&
                    this.maskExpression &&
                    this.dropSpecialCharacters !== false
                ) {
                    // eslint-disable-next-line no-param-reassign
                    inputValue = this._maskService._checkPrecision(
                        this._maskService.maskExpression,
                        inputValue as string
                    );
                }
                if (this._maskService.decimalMarker === MaskExpression.COMMA) {
                    // eslint-disable-next-line no-param-reassign
                    inputValue = inputValue
                        .toString()
                        .replace(MaskExpression.DOT, MaskExpression.COMMA);
                }
                if (this.maskExpression?.startsWith(MaskExpression.SEPARATOR) && this.leadZero) {
                    requestAnimationFrame(() => {
                        this._maskService.applyMask(
                            inputValue?.toString() ?? '',
                            this._maskService.maskExpression
                        );
                    });
                }
                this._maskService.isNumberValue = true;
            }

            if (typeof inputValue !== 'string') {
                // eslint-disable-next-line no-param-reassign
                inputValue = '';
            }

            this._inputValue = inputValue;
            this._setMask();

            if (
                (inputValue && this._maskService.maskExpression) ||
                (this._maskService.maskExpression &&
                    (this._maskService.prefix || this._maskService.showMaskTyped))
            ) {
                // Let the service we know we are writing value so that triggering onChange function won't happen during applyMask
                typeof this.inputTransformFn !== 'function'
                    ? (this._maskService.writingValue = true)
                    : '';
                this._maskService.formElementProperty = [
                    'value',
                    this._maskService.applyMask(inputValue, this._maskService.maskExpression),
                ];
                // Let the service know we've finished writing value
                typeof this.inputTransformFn !== 'function'
                    ? (this._maskService.writingValue = false)
                    : '';
            } else {
                this._maskService.formElementProperty = ['value', inputValue];
            }
            this._inputValue = inputValue;
        } else {
            console.warn(
                'Ngx-mask writeValue work with string | number, your current value:',
                typeof controlValue
            );
        }
    }

    public registerOnChange(fn: IOnChage): void {
        this._maskService.onChange = this.onChange = fn;
    }

    public registerOnTouched(fn: IOnTouch): void {
        this.onTouch = fn;
    }

    private _getActiveElement(document: DocumentOrShadowRoot = this.document): Element | null {
        const shadowRootEl = document?.activeElement?.shadowRoot;
        if (!shadowRootEl?.activeElement) {
            return document.activeElement;
        } else {
            return this._getActiveElement(shadowRootEl);
        }
    }

    public checkSelectionOnDeletion(el: HTMLInputElement): void {
        el.selectionStart = Math.min(
            Math.max(this.prefix.length, el.selectionStart as number),
            this._inputValue.length - this.suffix.length
        );
        el.selectionEnd = Math.min(
            Math.max(this.prefix.length, el.selectionEnd as number),
            this._inputValue.length - this.suffix.length
        );
    }

    /** It disables the input element */
    public setDisabledState(isDisabled: boolean): void {
        this._maskService.formElementProperty = ['disabled', isDisabled];
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private _applyMask(): any {
        this._maskService.maskExpression = this._maskService._repeatPatternSymbols(
            this._maskValue || ''
        );
        this._maskService.formElementProperty = [
            'value',
            this._maskService.applyMask(this._inputValue, this._maskService.maskExpression),
        ];
    }

    private _validateTime(value: string): ValidationErrors | null {
        const rowMaskLen: number = this._maskValue
            .split(MaskExpression.EMPTY_STRING)
            .filter((s: string) => s !== ':').length;
        if (!value) {
            return null; // Don't validate empty values to allow for optional form control
        }

        if (
            (+(value[value.length - 1] ?? -1) === 0 && value.length < rowMaskLen) ||
            value.length <= rowMaskLen - 2
        ) {
            return this._createValidationError(value);
        }

        return null;
    }

    private _getActualInputLength() {
        return (
            this._maskService.actualValue.length ||
            this._maskService.actualValue.length + this._maskService.prefix.length
        );
    }

    private _createValidationError(actualValue: string): ValidationErrors {
        return {
            mask: {
                requiredMask: this._maskValue,
                actualValue,
            },
        };
    }

    private _setMask() {
        this._maskExpressionArray.some((mask): boolean | void => {
            const specialChart: boolean = mask
                .split(MaskExpression.EMPTY_STRING)
                .some((char) => this._maskService.specialCharacters.includes(char));
            if (
                (specialChart && this._inputValue && !mask.includes(MaskExpression.LETTER_S)) ||
                mask.includes(MaskExpression.CURLY_BRACKETS_LEFT)
            ) {
                const test =
                    this._maskService.removeMask(this._inputValue)?.length <=
                    this._maskService.removeMask(mask)?.length;
                if (test) {
                    this._maskValue =
                        this.maskExpression =
                        this._maskService.maskExpression =
                            mask.includes(MaskExpression.CURLY_BRACKETS_LEFT)
                                ? this._maskService._repeatPatternSymbols(mask)
                                : mask;
                    return test;
                } else {
                    const expression =
                        this._maskExpressionArray[this._maskExpressionArray.length - 1] ??
                        MaskExpression.EMPTY_STRING;
                    this._maskValue =
                        this.maskExpression =
                        this._maskService.maskExpression =
                            expression.includes(MaskExpression.CURLY_BRACKETS_LEFT)
                                ? this._maskService._repeatPatternSymbols(expression)
                                : expression;
                }
            } else {
                const check: boolean = this._maskService
                    .removeMask(this._inputValue)
                    ?.split(MaskExpression.EMPTY_STRING)
                    .every((character, index) => {
                        const indexMask = mask.charAt(index);
                        return this._maskService._checkSymbolMask(character, indexMask);
                    });
                if (check) {
                    this._maskValue = this.maskExpression = this._maskService.maskExpression = mask;
                    return check;
                }
            }
        });
    }
}
